// src/admin/components/BranchView.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  LinearProgress,
  Alert,
  CircularProgress,
  IconButton,
  Button,
  Stack,
  Snackbar,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Warehouse as WarehouseIcon,
  DirectionsCar as CarIcon,
  Close as CloseIcon,
  TransferWithinAStation as TransferIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { Warehouse, Product, CarStatus } from '../../../enums/product';
import { WarehouseService } from '../branch/branchService';
import { ProductService } from '../car/carService';
import { useAuth } from '../../../contexts/AuthContext';
import TransferDialog from './TransferDialog';

interface WarehouseViewProps {
  warehouseId: string;
  onClose: () => void;
  onEdit: (warehouse: Warehouse) => void;
  canTransfer?: boolean;
}

// ── Theme-aware chip style helper ─────────────────────────────────────────────
const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning:   { bg: 'rgba(255,167,38,0.12)',  border: 'rgba(255,167,38,0.4)',   text: '#b76e00', iconColor: '#e68900' },
    info:      { bg: 'rgba(41,182,246,0.12)',   border: 'rgba(41,182,246,0.4)',   text: '#0277bd', iconColor: '#039be5' },
    primary:   { bg: 'rgba(33,150,243,0.12)',   border: 'rgba(33,150,243,0.4)',   text: '#1565c0', iconColor: '#1976d2' },
    success:   { bg: 'rgba(102,187,106,0.15)',  border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error:     { bg: 'rgba(239,83,80,0.12)',    border: 'rgba(239,83,80,0.4)',    text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)',   border: 'rgba(171,71,188,0.35)',  text: '#6a1b9a', iconColor: '#8e24aa' },
    default:   { bg: 'rgba(158,158,158,0.12)',  border: 'rgba(158,158,158,0.4)',  text: '#424242', iconColor: '#757575' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning:   { bg: 'rgba(255,167,38,0.22)',  border: 'rgba(255,167,38,0.55)',  text: '#ffcc80', iconColor: '#ffa726' },
    info:      { bg: 'rgba(41,182,246,0.20)',   border: 'rgba(41,182,246,0.50)',  text: '#81d4fa', iconColor: '#29b6f6' },
    primary:   { bg: 'rgba(33,150,243,0.22)',   border: 'rgba(33,150,243,0.50)',  text: '#90caf9', iconColor: '#42a5f5' },
    success:   { bg: 'rgba(102,187,106,0.22)',  border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error:     { bg: 'rgba(239,83,80,0.22)',    border: 'rgba(239,83,80,0.50)',   text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)',   border: 'rgba(171,71,188,0.50)',  text: '#ce93d8', iconColor: '#ab47bc' },
    default:   { bg: 'rgba(158,158,158,0.22)',  border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

const statusChipColor = (status?: CarStatus): string => {
  switch (status) {
    case 'In Stock': return 'success';
    case 'Reserved': return 'warning';
    case 'Sold': return 'secondary';
    case 'Delivered': return 'info';
    case 'In Transit (Import)': return 'default';
    default: return 'default';
  }
};
// ─────────────────────────────────────────────────────────────────────────────

const WarehouseView: React.FC<WarehouseViewProps> = ({
  warehouseId,
  onClose,
  onEdit,
  canTransfer = false,
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [warehouse, setWarehouse] = useState<Warehouse | null>(null);
  const [cars, setCars] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedCar, setSelectedCar] = useState<Product | null>(null);
  const [successMsg, setSuccessMsg] = useState('');

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  useEffect(() => {
    loadWarehouseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId]);

  const loadWarehouseData = async () => {
    try {
      setLoading(true);
      const [warehouseData, carsData] = await Promise.all([
        WarehouseService.getWarehouse(warehouseId),
        ProductService.getProductsByBranch(warehouseId),
      ]);
      // `capacity.used` stored on the warehouse doc is a manually-typed
      // number nothing keeps in sync — compute the real occupied count from
      // the cars actually assigned to this branch instead of trusting it.
      if (warehouseData) {
        const occupyingStatuses = new Set(['In Stock', 'Reserved', 'Sold']);
        const used = carsData.filter(c => c.status && occupyingStatuses.has(c.status)).length;
        const total = warehouseData.capacity?.total || 0;
        setWarehouse({
          ...warehouseData,
          capacity: { total, used, available: Math.max(total - used, 0) },
        });
      } else {
        setWarehouse(warehouseData);
      }
      setCars(carsData);
    } catch (err: any) {
      console.error('Error loading show room data:', err);
      setError('Failed to load show room data');
    } finally {
      setLoading(false);
    }
  };

  const handleMoveVehicle = (car: Product) => {
    setSelectedCar(car);
    setTransferDialogOpen(true);
  };

  const handleTransferSubmit = async (toWarehouseId: string, toWarehouseName: string) => {
    if (!selectedCar?.id) return;
    await ProductService.updateProductBranch(
      selectedCar.id,
      selectedCar.name,
      toWarehouseId,
      toWarehouseName,
      currentUser?.uid || '',
      currentUser?.displayName || currentUser?.email || 'Admin'
    );
    setTransferDialogOpen(false);
    setSelectedCar(null);
    setSuccessMsg(`Moved '${selectedCar.name}' to ${toWarehouseName}`);
    await loadWarehouseData();
  };

  const stats = {
    total: cars.length,
    inStock: cars.filter(c => c.status === 'In Stock').length,
    reserved: cars.filter(c => c.status === 'Reserved').length,
    sold: cars.filter(c => c.status === 'Sold' || c.status === 'Delivered').length,
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !warehouse) {
    return (
      <Alert severity="error" onClose={onClose}>
        {error || 'Show Room not found'}
      </Alert>
    );
  }

  const utilizationRate = warehouse.capacity.total > 0
    ? Math.round((warehouse.capacity.used / warehouse.capacity.total) * 100)
    : 0;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{
        p: 3,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <WarehouseIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" gutterBottom>
                {warehouse.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {warehouse.code} • {warehouse.address.city}, {warehouse.address.country}
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              startIcon={<OpenInNewIcon />}
              variant="outlined"
              size="small"
              onClick={() => navigate(`/admin/products?branch=${warehouseId}`)}
            >
              Open in Vehicle Inventory
            </Button>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {/* Stats Cards */}
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 1.25, height: '100%' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Capacity Utilization
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={utilizationRate}
                  sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                  color={utilizationRate > 90 ? 'error' : utilizationRate > 70 ? 'warning' : 'success'}
                />
                <Typography variant="body2" fontWeight={700}>
                  {utilizationRate}%
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                {warehouse.capacity.used.toLocaleString()} / {warehouse.capacity.total.toLocaleString()} slots used
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 1.25, height: '100%' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Total Vehicles
              </Typography>
              <Typography variant="h5" color="primary" sx={{ mb: 0.5, fontWeight: 700 }}>
                {stats.total}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                Vehicles assigned to this branch
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 1.25, height: '100%' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                In Stock
              </Typography>
              <Typography variant="h5" color="primary" sx={{ mb: 0.5, fontWeight: 700 }}>
                {stats.inStock}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                Available for sale right now
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 1.25, height: '100%' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Status Breakdown
              </Typography>
              <Stack spacing={0.75} sx={{ mt: 0.5 }}>
                <Chip
                  label={`${stats.reserved} Reserved`}
                  size="small"
                  variant="outlined"
                  sx={getSoftChipSx('warning', isDark)}
                />
                <Chip
                  label={`${stats.sold} Sold / Delivered`}
                  size="small"
                  variant="outlined"
                  sx={getSoftChipSx('secondary', isDark)}
                />
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* Vehicles Table */}
        <Card>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">
              Vehicles at this Branch ({cars.length})
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>VIN / Chassis</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell>Condition</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cars.map((car) => (
                  <TableRow key={car.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar
                          variant="rounded"
                          src={car.images?.[0]}
                          sx={{ width: 44, height: 44, bgcolor: 'action.hover' }}
                        >
                          <CarIcon fontSize="small" />
                        </Avatar>
                        <Typography variant="body2" fontWeight={500}>
                          {car.name}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {car.sku}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={car.status || 'Unknown'}
                        size="small"
                        variant="outlined"
                        sx={getSoftChipSx(statusChipColor(car.status), isDark)}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {car.price?.toLocaleString()}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {car.condition || '—'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {canTransfer && (
                        <IconButton
                          size="small"
                          onClick={() => handleMoveVehicle(car)}
                          title="Move to another branch"
                        >
                          <TransferIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}

                {cars.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No vehicles assigned to this branch yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>

      {/* Transfer Dialog */}
      {warehouse && (
        <TransferDialog
          open={transferDialogOpen}
          onClose={() => setTransferDialogOpen(false)}
          onSubmit={handleTransferSubmit}
          car={selectedCar}
          sourceWarehouse={warehouse}
        />
      )}

      <Snackbar
        open={!!successMsg}
        autoHideDuration={4000}
        onClose={() => setSuccessMsg('')}
        message={successMsg}
      />
    </Box>
  );
};

export default WarehouseView;
